import {
  Children,
  cloneElement,
  createElement,
  forwardRef,
  isValidElement,
  type ComponentRef,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import { useWindowDimensions } from "@luce/core";

import { Icon } from "../../atoms/icons";
import { Typography } from "../../atoms/typography";
import { cn, platformProps } from "../../lib";
import { Box, composeRefs, Pressable } from "../../primitives";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import { asChildSelectedProps } from "./as-child-selected";
import { useSidebarMenuContext } from "./context";
import { hrefRowIsAnchor } from "./href-row";
import {
  sidebarNavItemCollapsedBaseClasses,
  sidebarNavItemCollapsedRadiusClasses,
  sidebarNavItemExpandedBaseClasses,
  sidebarNavItemExpandedRadiusClasses,
  sidebarNavItemExpandedStateClasses,
  sidebarNavItemIconColorClass,
  sidebarNavItemIconSize,
  sidebarNavItemLabelColor,
  sidebarNavItemLabelVariant,
  sidebarNavItemSelectedBorderClasses,
  sidebarNavItemSupportingTextColor,
} from "./lib";
import type { SidebarNavItemIcon, SidebarNavItemProps } from "./types";

/** The label's text, for the collapsed chip's name and tooltip: strings and
 *  numbers, also inside elements (`<Text>Overview</Text>`). A component that
 *  renders its own text has none to find. */
const extractLabelText = (nodes: ReactNode): string | undefined => {
  const parts: string[] = [];
  const collect = (node: ReactNode) => {
    if (typeof node === "string" || typeof node === "number") parts.push(String(node));
    else if (Array.isArray(node)) node.forEach(collect);
    else if (isValidElement<{ children?: ReactNode }>(node)) collect(node.props.children);
  };
  collect(nodes);
  return parts.length ? parts.join("") : undefined;
};

const renderLeadingIcon = (
  icon: SidebarNavItemIcon,
  state: "idle" | "selected",
  twoLine: boolean,
  isDesktop: boolean,
) => {
  const size =
    sidebarNavItemIconSize[twoLine ? "twoLine" : "oneLine"][isDesktop ? "desktop" : "mobile"];
  const className = cn("shrink-0", sidebarNavItemIconColorClass[state]);
  const weight = state === "selected" ? "fill" : "regular";

  if (typeof icon === "string") {
    return <Icon name={icon} size={size} weight={weight} className={className} />;
  }

  return createElement(icon, { size, weight, className });
};

// The ref is the interactive host's: the button, the link, or the consumer's
// element with `asChild` (ADR-007)
export const SidebarNavItem = forwardRef<ComponentRef<typeof Box>, SidebarNavItemProps>(
  function SidebarNavItem(
    {
      icon,
      children,
      supportingText,
      trailing,
      selected = false,
      onPress,
      href,
      asChild = false,
      className,
      testID,
    },
    ref,
  ) {
    const { collapsed } = useSidebarMenuContext();
    const { isDesktop } = useWindowDimensions();
    const state = selected ? "selected" : "idle";
    const twoLine = supportingText != null && supportingText !== false;

    // When `asChild`, the first element child is the wrapper we inject into (a
    // router `<Link>`); the label is the children beside it or, with none
    // there, the wrapper's own (`<Link to="/x">Overview</Link>`)
    const childArray = Children.toArray(children);
    const wrapperElement = asChild
      ? (childArray.find(isValidElement) as ReactElement<Record<string, unknown>> | undefined)
      : undefined;
    const siblingLabel = childArray.filter((child) => child !== wrapperElement);
    const labelNodes: ReactNode = !asChild
      ? children
      : siblingLabel.length
        ? siblingLabel
        : (wrapperElement?.props["children"] as ReactNode);

    const labelText = extractLabelText(labelNodes);
    const trailingElement: ReactElement | null =
      trailing && isValidElement(trailing) ? trailing : null;

    const radiusClassName = (
      collapsed ? sidebarNavItemCollapsedRadiusClasses : sidebarNavItemExpandedRadiusClasses
    )[twoLine ? "twoLine" : "oneLine"];
    const rowClassName = cn(
      collapsed ? sidebarNavItemCollapsedBaseClasses : sidebarNavItemExpandedBaseClasses,
      radiusClassName,
      sidebarNavItemExpandedStateClasses[state],
      className,
    );

    const selectedBorder = selected ? (
      <Box className={cn(sidebarNavItemSelectedBorderClasses, radiusClassName)} />
    ) : null;
    const content = collapsed ? (
      <>
        {selectedBorder}
        {renderLeadingIcon(icon, state, twoLine, isDesktop)}
      </>
    ) : (
      <>
        {selectedBorder}
        {renderLeadingIcon(icon, state, twoLine, isDesktop)}
        {/* One line ending in "…": `truncate` on web, `numberOfLines` on native */}
        <Box className="flex min-w-0 flex-1 flex-col">
          <Typography
            variant={sidebarNavItemLabelVariant[state]}
            color={sidebarNavItemLabelColor[state]}
            numberOfLines={1}
            className="truncate"
          >
            {labelNodes}
          </Typography>
          {twoLine ? (
            // Figma's `text/caption/md`, `padding-xxs` below the label (2, and
            // 4 from 768)
            <Typography
              variant="caption-md"
              color={sidebarNavItemSupportingTextColor}
              numberOfLines={1}
              className="mt-spacing-xxs md:mt-spacing-xs truncate"
            >
              {supportingText}
            </Typography>
          ) : null}
        </Box>
        {trailingElement ? <Box className="ml-auto shrink-0">{trailingElement}</Box> : null}
      </>
    );

    const accessibilityLabel = collapsed ? labelText : undefined;
    const ariaCurrentProps: Record<string, string> = selected ? { "aria-current": "page" } : {};

    let interactive: ReactElement;
    if (asChild && wrapperElement) {
      // Inject the row content + nav styling + a11y into a clone of the
      // consumer's element (the router `<Link>`). cloneElement (not SlotPressable)
      // because we OVERRIDE the child's children with our composed content rather
      // than preserving them.
      const wrapperClassName = wrapperElement.props["className"] as string | undefined;
      const wrapperRef = wrapperElement.props["ref"] as Ref<unknown> | undefined;
      const wrapperOnClick = wrapperElement.props["onClick"] as
        | ((event: unknown) => void)
        | undefined;
      // The element's own name wins; otherwise a collapsed chip takes the label
      const nameProps =
        accessibilityLabel && wrapperElement.props["aria-label"] == null
          ? { "aria-label": accessibilityLabel }
          : {};
      interactive = cloneElement(
        wrapperElement,
        {
          ref: composeRefs(ref as Ref<unknown>, wrapperRef),
          ...platformProps({ testID, slot: "sidebar-nav-item" }),
          ...ariaCurrentProps,
          ...asChildSelectedProps(
            selected,
            wrapperElement.props["accessibilityState"] as object | undefined,
          ),
          ...nameProps,
          // The element's own handler first, then the row's
          onClick:
            wrapperOnClick || onPress
              ? (event: unknown) => {
                  wrapperOnClick?.(event);
                  onPress?.();
                }
              : undefined,
          className: cn("flex no-underline", rowClassName, wrapperClassName),
        },
        content,
      );
    } else if (href && hrefRowIsAnchor) {
      interactive = (
        <Box
          ref={ref}
          as="a"
          {...platformProps({ testID, slot: "sidebar-nav-item" })}
          accessibilityRole="link"
          accessibilityLabel={accessibilityLabel}
          accessibilityState={{ selected }}
          {...ariaCurrentProps}
          href={href}
          onClick={onPress}
          className={cn("flex no-underline", rowClassName)}
        >
          {content}
        </Box>
      );
    } else {
      interactive = (
        <Pressable
          // A button on web; the ref's type covers the link and `asChild` too
          ref={ref as Ref<ComponentRef<typeof Pressable>>}
          {...platformProps({ testID, slot: "sidebar-nav-item" })}
          // An `href` row on iOS and Android, which can't follow it
          accessibilityRole={href ? "link" : "button"}
          accessibilityLabel={accessibilityLabel}
          accessibilityState={{ selected }}
          {...ariaCurrentProps}
          onClick={onPress}
          className={rowClassName}
        >
          {content}
        </Pressable>
      );
    }

    // Collapsed icon-only chips lose their visible label, so surface it on hover
    // via a side Tooltip (the accessible name is already on `aria-label`).
    const row =
      collapsed && labelText ? (
        <Tooltip>
          <TooltipTrigger asChild>{interactive}</TooltipTrigger>
          <TooltipContent side="right">{labelText}</TooltipContent>
        </Tooltip>
      ) : (
        interactive
      );

    return (
      <Box as="li" className="list-none">
        {row}
      </Box>
    );
  },
);

SidebarNavItem.displayName = "SidebarNavItem";
